(ns practitest.frontend.integration-settings.entity-mapping.events
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.components.notifications :as notifications]
            [ajax.core :as ajax]))

(def ^:private default-requirements-types #{"User Story"})
(def ^:private default-issues-types #{"Bug" "Task"})

(defn- apply-defaults
  [work-item-types]
  (let [all-ids     (map :id work-item-types)
        req-types   (vec (filter #(contains? default-requirements-types %) all-ids))
        issue-types (vec (filter #(contains? default-issues-types %) all-ids))]
    {:requirements {:ex_type req-types}
     :issues       {:ex_type issue-types}}))

(defn- build-sp-map [types]
  (into {} (map (fn [t] [(:id t) (boolean (:supports_parent t))]) types)))

(defn- new-project-mapping [work-item-types-map pid-key]
  (let [pid-str (name pid-key)
        types   (get work-item-types-map pid-str)]
    (if types
      (assoc (apply-defaults types) :type_supports_parent (build-sp-map types))
      {:requirements       {:ex_type []}
       :issues             {:ex_type []}
       :_pending_defaults? true})))

;; Serialized lazy-loading of types. Azure fetches per project-id; ClickUp per space-id.
(defn- chain-next-fetch [db]
  (let [bugtracker (get-in db [:entity-mapping :bugtracker])
        cached     (get-in db [:entity-mapping :project-work-item-types] {})
        loading    (get-in db [:entity-mapping :project-types-loading] #{})
        failed     (get-in db [:entity-mapping :project-types-failed] #{})]
    (if (= bugtracker "ClickUp")
      (let [space-ids (->> (concat
                            (map :space_id (get-in db [:entity-mapping :configured-lists] []))
                            (get-in db [:entity-mapping :selected-space-ids] #{}))
                           (remove nil?)
                           distinct)]
        (when-let [sid (->> space-ids
                            (remove #(contains? cached %))
                            (remove #(contains? loading %))
                            (remove #(contains? failed %))
                            first)]
          [[:dispatch [::fetch-space-task-types sid]]]))
      (let [selected (get-in db [:entity-mapping :selected-project-ids] #{})]
        (when-let [pid (->> selected
                            (map name)
                            (remove #(contains? cached %))
                            (remove #(contains? loading %))
                            (remove #(contains? failed %))
                            first)]
          [[:dispatch [::fetch-project-work-item-types pid]]])))))

(re-frame/reg-event-db
 ::initialize-db
 (fn [db [_ {:keys [project-id integration-id integration-name bugtracker]}]]
   (assoc db :entity-mapping
          {:project-id              project-id
           :integration-id          integration-id
           :integration-name        integration-name
           :bugtracker              bugtracker
           :loading?                true
           :projects-loading?       true
           :selected-project-ids    #{}
           :available-projects      []
           :configured-lists        []
           ;; ClickUp space/list selection
           :available-spaces        []
           :spaces-loading?         false
           :selected-space-ids      #{}
           :available-lists-by-space {}
           :lists-loading           #{}
           :project-work-item-types {}
           :project-types-loading   #{}
           :project-types-failed    #{}
           :data                    nil
           :errors                  []})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:http-xhrio {:method          :get
                   :uri             (broutes/get-url "two_way_integration_settings#entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-entity-mapping-success]
                   :on-failure      [::fetch-entity-mapping-failure]}})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-success
 (fn [{:keys [db]} [_ response]]
   (let [bugtracker       (get-in db [:entity-mapping :bugtracker])
         project-mappings (or (:project_mappings response) {})
         mapped-pids      (keys project-mappings)
         configured-lists (vec (:configured_lists response []))
         pre-space-ids    (when (= bugtracker "ClickUp")
                            (->> configured-lists (keep :space_id) (filter seq) set))
         seeded-lists     (when (seq pre-space-ids)
                            (->> configured-lists
                                 (filter #(seq (:space_id %)))
                                 (group-by :space_id)
                                 (reduce-kv (fn [m sid lists]
                                              (assoc m (keyword sid)
                                                     (mapv (fn [l] {:id (:id l) :label (:name l) :space_id sid}) lists)))
                                            {})))
         db'              (cond-> (update db :entity-mapping merge
                                          {:loading?                   false
                                           :data                       response
                                           :saved-data                 response
                                           :selected-project-ids       (set mapped-pids)
                                           :saved-selected-project-ids (set mapped-pids)
                                           :configured-lists           configured-lists
                                           :errors                     []})
                            pre-space-ids (assoc-in [:entity-mapping :selected-space-ids] pre-space-ids)
                            pre-space-ids (assoc-in [:entity-mapping :saved-selected-space-ids] pre-space-ids)
                            seeded-lists  (assoc-in [:entity-mapping :available-lists-by-space] seeded-lists))]
     {:db db'
      :fx (cond-> (or (chain-next-fetch db') [])
            (= bugtracker "ClickUp") (conj [:dispatch [::fetch-available-spaces]])
            (seq pre-space-ids) (conj [:dispatch [::fetch-lists-for-spaces (vec pre-space-ids)]]))})))

(re-frame/reg-event-fx
 ::fetch-entity-mapping-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.fetch-failed"
                                                "Failed to load entity mapping"))]
     {:db       (update db :entity-mapping merge {:loading? false})
      :dispatch [::notifications/error2 error-message]})))

;; Azure only
(re-frame/reg-event-fx
 ::fetch-available-projects
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:db         (assoc-in db [:entity-mapping :projects-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "azure_integration_settings#available_azure_projects"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-projects-success]
                   :on-failure      [::fetch-available-projects-failure]}})))

(re-frame/reg-event-db
 ::fetch-available-projects-success
 (fn [db [_ response]]
   (update db :entity-mapping merge
           {:projects-loading?  false
            :available-projects (vec (:available_projects response []))})))

(re-frame/reg-event-fx
 ::fetch-available-projects-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (format-translation (get-translation "bugtracker.integration-settings.error.projects-fetch-failed"
                                                                    "Failed to load %{bugtracker} projects")
                                                   :bugtracker "Azure DevOps"))]
     {:db       (assoc-in db [:entity-mapping :projects-loading?] false)
      :dispatch [::notifications/error2 error-message]})))

;; Azure only
(re-frame/reg-event-fx
 ::fetch-project-work-item-types
 (fn [{:keys [db]} [_ azure-project-id]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         loading-set     (get-in db [:entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:entity-mapping :project-work-item-types] {})
         already-active? (or (contains? loading-set azure-project-id)
                             (contains? types-cache azure-project-id))]
     (if already-active?
       {}
       {:db         (update-in db [:entity-mapping :project-types-loading] (fnil conj #{}) azure-project-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "two_way_integration_settings#entity_mapping_work_item_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:azure_project_id azure-project-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-project-work-item-types-success azure-project-id]
                     :on-failure      [::fetch-project-work-item-types-failure azure-project-id]}}))))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-success
 (fn [{:keys [db]} [_ azure-project-id response]]
   (let [pid-key         (keyword azure-project-id)
         work-item-types (vec response)
         has-mapping?    (some? (get-in db [:entity-mapping :data :project_mappings pid-key]))
         db'             (cond-> db
                           true         (update-in [:entity-mapping :project-work-item-types] assoc azure-project-id work-item-types)
                           true         (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                           (and has-mapping?
                                (get-in db [:entity-mapping :data :project_mappings pid-key :_pending_defaults?]))
                           (update-in [:entity-mapping :data :project_mappings pid-key]
                                      (fn [m]
                                        (-> (apply-defaults work-item-types)
                                            (assoc :project_name (:project_name m))
                                            (assoc :type_supports_parent (build-sp-map work-item-types))
                                            (dissoc :_pending_defaults?)))))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-fx
 ::fetch-project-work-item-types-failure
 (fn [{:keys [db]} [_ azure-project-id _response]]
   (let [db' (-> db
                 (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) azure-project-id)
                 (update-in [:entity-mapping :project-types-failed] (fnil conj #{}) azure-project-id))]
     {:db db'
      :fx (into [[:dispatch [::notifications/error2
                             (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                  "Failed to load %{bugtracker} types for one of the projects")
                                                :bugtracker "Azure DevOps")]]]
                (chain-next-fetch db'))})))

;; ClickUp only
(re-frame/reg-event-fx
 ::fetch-space-task-types
 (fn [{:keys [db]} [_ space-id]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         loading-set     (get-in db [:entity-mapping :project-types-loading] #{})
         types-cache     (get-in db [:entity-mapping :project-work-item-types] {})
         already-active? (or (contains? loading-set space-id)
                             (contains? types-cache space-id))]
     (if already-active?
       {}
       {:db         (update-in db [:entity-mapping :project-types-loading] (fnil conj #{}) space-id)
        :http-xhrio {:method          :get
                     :uri             (broutes/get-url "clickup_integration_settings#entity_mapping_space_task_types"
                                                       {:project_id project-id
                                                        :id         integration-id})
                     :params          {:space_id space-id}
                     :response-format (ajax/json-response-format {:keywords? true})
                     :on-success      [::fetch-space-task-types-success space-id]
                     :on-failure      [::fetch-space-task-types-failure space-id]}}))))

(re-frame/reg-event-fx
 ::fetch-space-task-types-success
 (fn [{:keys [db]} [_ space-id response]]
   (let [task-types (vec (if (sequential? response) response (:data response [])))
         db'        (-> db
                        (update-in [:entity-mapping :project-work-item-types] assoc space-id task-types)
                        (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) space-id))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-fx
 ::fetch-space-task-types-failure
 (fn [{:keys [db]} [_ space-id _response]]
   (let [db' (-> db
                 (update-in [:entity-mapping :project-types-loading] (fnil disj #{}) space-id)
                 (update-in [:entity-mapping :project-types-failed] (fnil conj #{}) space-id))]
     {:db db'
      :fx (into [[:dispatch [::notifications/error2
                             (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                                  "Failed to load %{bugtracker} types for one of the projects")
                                                :bugtracker "ClickUp")]]]
                (chain-next-fetch db'))})))

;; ClickUp only — space/list selection

(re-frame/reg-event-fx
 ::fetch-available-spaces
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)]
     {:db         (assoc-in db [:entity-mapping :spaces-loading?] true)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "clickup_integration_settings#available_clickup_spaces"
                                                     {:project_id project-id :id integration-id})
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-available-spaces-success]
                   :on-failure      [::fetch-available-spaces-failure]}})))

(re-frame/reg-event-fx
 ::fetch-available-spaces-success
 (fn [{:keys [db]} [_ response]]
   (let [spaces           (vec (:available_spaces response []))
         selected-ids     (get-in db [:entity-mapping :selected-space-ids] #{})
         space-ids-to-fetch (->> selected-ids
                                 (remove #(contains? (get-in db [:entity-mapping :available-lists-by-space] {}) %))
                                 seq)
         db'              (update db :entity-mapping merge
                                  {:available-spaces spaces
                                   :spaces-loading?  false})]
     {:db db'
      :fx (when space-ids-to-fetch
            [[:dispatch [::fetch-lists-for-spaces (vec space-ids-to-fetch)]]])})))

(re-frame/reg-event-db
 ::fetch-available-spaces-failure
 (fn [db [_ _response]]
   (assoc-in db [:entity-mapping :spaces-loading?] false)))

(re-frame/reg-event-fx
 ::fetch-lists-for-spaces
 (fn [{:keys [db]} [_ space-ids]]
   (let [{:keys [project-id integration-id]} (:entity-mapping db)
         new-loading (reduce conj (get-in db [:entity-mapping :lists-loading] #{}) space-ids)]
     {:db         (assoc-in db [:entity-mapping :lists-loading] new-loading)
      :http-xhrio {:method          :get
                   :uri             (broutes/get-url "clickup_integration_settings#available_clickup_lists"
                                                     {:project_id project-id :id integration-id})
                   :params          {:space_ids space-ids}
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::fetch-lists-for-spaces-success space-ids]
                   :on-failure      [::fetch-lists-for-spaces-failure space-ids]}})))

(re-frame/reg-event-db
 ::fetch-lists-for-spaces-success
 (fn [db [_ space-ids response]]
   (let [lists-map      (:lists_by_space response {})
         new-loading    (reduce disj (get-in db [:entity-mapping :lists-loading] #{}) space-ids)
         updated-cache  (reduce (fn [cache sid]
                                  (let [api-lists (get lists-map (keyword sid))]
                                    (if (seq api-lists)
                                      (assoc cache (keyword sid)
                                             (vec (map (fn [l] {:id (:id l) :label (:name l) :space_id sid})
                                                       api-lists)))
                                      cache)))
                                (get-in db [:entity-mapping :available-lists-by-space] {})
                                space-ids)]
     (-> db
         (assoc-in [:entity-mapping :lists-loading] new-loading)
         (assoc-in [:entity-mapping :available-lists-by-space] updated-cache)))))

(re-frame/reg-event-fx
 ::fetch-lists-for-spaces-failure
 (fn [{:keys [db]} [_ space-ids _response]]
   (let [new-loading (reduce disj (get-in db [:entity-mapping :lists-loading] #{}) space-ids)]
     {:db       (assoc-in db [:entity-mapping :lists-loading] new-loading)
      :dispatch [::notifications/error2
                 (format-translation (get-translation "bugtracker.integration-settings.error.types-fetch-failed"
                                                      "Failed to load %{bugtracker} types for one of the projects")
                                     :bugtracker "ClickUp")]})))

(re-frame/reg-event-fx
 ::update-selected-spaces
 (fn [{:keys [db]} [_ selected-space-ids]]
   (let [prev-space-ids   (get-in db [:entity-mapping :selected-space-ids] #{})
         added-spaces     (remove #(contains? prev-space-ids %) selected-space-ids)
         removed-spaces   (remove #(contains? (set selected-space-ids) %) prev-space-ids)
         lists-by-space   (get-in db [:entity-mapping :available-lists-by-space] {})
         removed-list-ids (set (mapcat (fn [sid]
                                         (map (comp keyword :id) (get lists-by-space (keyword sid) [])))
                                       removed-spaces))
         current-selected (get-in db [:entity-mapping :selected-project-ids] #{})
         new-selected     (remove #(contains? removed-list-ids %) current-selected)
         new-mappings     (reduce dissoc
                                  (get-in db [:entity-mapping :data :project_mappings] {})
                                  removed-list-ids)
         db'              (-> db
                              (assoc-in [:entity-mapping :selected-space-ids] (set selected-space-ids))
                              (assoc-in [:entity-mapping :selected-project-ids] (set new-selected))
                              (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (when (seq added-spaces)
            [[:dispatch [::fetch-lists-for-spaces (vec added-spaces)]]])})))

(re-frame/reg-event-fx
 ::update-selected-lists
 (fn [{:keys [db]} [_ selected-list-ids]]
   (let [lists-by-space  (get-in db [:entity-mapping :available-lists-by-space] {})
         all-lists       (mapcat val lists-by-space)
         lists-index     (into {} (map (fn [l] [(:id l) l]) all-lists))
         current-mappings (get-in db [:entity-mapping :data :project_mappings] {})
         new-mappings    (reduce
                          (fn [acc lid]
                            (assoc acc (keyword lid)
                                   (get current-mappings (keyword lid)
                                        {:requirements {:ex_type []}
                                         :issues       {:ex_type ["Task"]}})))
                          {}
                          selected-list-ids)
         db'             (-> db
                             (assoc-in [:entity-mapping :selected-project-ids] (set selected-list-ids))
                             (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (or (chain-next-fetch db') [])})))

(re-frame/reg-event-db
 ::toggle-requirements-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :requirements_sync_enabled] enabled?)))

(re-frame/reg-event-db
 ::toggle-issues-sync
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :issues_sync_enabled] enabled?)))

;; Azure only
(re-frame/reg-event-fx
 ::update-selected-projects
 (fn [{:keys [db]} [_ selected-project-ids]]
   (let [current-mappings    (get-in db [:entity-mapping :data :project_mappings] {})
         available-projects  (get-in db [:entity-mapping :available-projects] [])
         work-item-types-map (get-in db [:entity-mapping :project-work-item-types] {})
         projects-by-id      (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
         new-mappings
         (reduce
          (fn [acc pid-key]
            (let [mapping (if (contains? current-mappings pid-key)
                            (get current-mappings pid-key)
                            (new-project-mapping work-item-types-map pid-key))
                  p-name  (get projects-by-id pid-key)]
              (cond-> (assoc acc pid-key mapping)
                p-name (assoc-in [pid-key :project_name] p-name))))
          {}
          selected-project-ids)
         db' (-> db
                 (assoc-in [:entity-mapping :selected-project-ids] selected-project-ids)
                 (assoc-in [:entity-mapping :data :project_mappings] new-mappings))]
     {:db db'
      :fx (or (when (empty? (get-in db' [:entity-mapping :project-types-loading] #{}))
                (chain-next-fetch db'))
              [])})))

(re-frame/reg-event-db
 ::update-project-mapping
 (fn [db [_ project-id entity-type field value]]
   (assoc-in db [:entity-mapping :data :project_mappings
                 (keyword project-id) entity-type field]
             value)))

(re-frame/reg-event-db
 ::toggle-project-entity-status
 (fn [db [_ project-id entity-type]]
   (let [bugtracker      (get-in db [:entity-mapping :bugtracker])
         pid-key         (keyword project-id)
         current-mappings (get-in db [:entity-mapping :data :project_mappings] {})
         entity-path     [pid-key entity-type]
         current-entity  (get-in current-mappings entity-path)
         is-enabling?    (:disabled current-entity false)
         work-item-types (get-in db [:entity-mapping :project-work-item-types project-id])
         updated-entity  (if is-enabling?
                           (if (and (= bugtracker "AzureDevops") work-item-types)
                             {:ex_type (vec (filter #(contains? (if (= entity-type :requirements)
                                                                  default-requirements-types
                                                                  default-issues-types) %)
                                                    (map :id work-item-types)))}
                             {:ex_type []})
                           {:ex_type [] :disabled true})]
     (assoc-in db [:entity-mapping :data :project_mappings]
               (assoc-in current-mappings entity-path updated-entity)))))

(re-frame/reg-event-db
 ::toggle-allow-issue-creation
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :allow_issue_creation] enabled?)))

(re-frame/reg-event-db
 ::toggle-use-pt-id
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :use_pt_id] enabled?)))

(re-frame/reg-event-db
 ::toggle-skip-mapping-manual-import
 (fn [db [_ enabled?]]
   (assoc-in db [:entity-mapping :data :skip_mapping_in_manual_import] enabled?)))

(re-frame/reg-event-fx
 ::save-settings
 (fn [{:keys [db]} [_]]
   (let [{:keys [project-id integration-id data bugtracker available-projects]} (:entity-mapping db)
         work-item-types-map (get-in db [:entity-mapping :project-work-item-types] {})
         payload (cond
                   (= bugtracker "AzureDevops")
                   (let [projects-by-id    (into {} (map (fn [p] [(keyword (:id p)) (:name p)]) available-projects))
                         enriched-mappings (reduce-kv
                                            (fn [m pid _mapping]
                                              (let [p-name     (get projects-by-id pid)
                                                    types-list (get work-item-types-map (name pid) [])
                                                    sp-map     (when (seq types-list) (build-sp-map types-list))]
                                                (cond-> m
                                                  p-name (assoc-in [pid :project_name] p-name)
                                                  sp-map (assoc-in [pid :type_supports_parent] sp-map))))
                                            (:project_mappings data {})
                                            (:project_mappings data {}))]
                     (assoc data :project_mappings enriched-mappings))
                   (= bugtracker "ClickUp")
                   (let [lists-by-space     (get-in db [:entity-mapping :available-lists-by-space] {})
                         configured-lists   (get-in db [:entity-mapping :configured-lists] [])
                         all-lists-index    (into {}
                                                  (concat
                                                   (map (fn [l] [(:id l) l]) configured-lists)
                                                   (mapcat (fn [[_sid lists]]
                                                             (map (fn [l] [(:id l) l]) lists))
                                                           lists-by-space)))
                         enriched-mappings  (reduce-kv
                                             (fn [m pid mapping]
                                               (let [lid-str (name pid)
                                                     list    (get all-lists-index lid-str)
                                                     l-name  (or (:label list) (:name list)
                                                                  (get-in mapping [:list_name])
                                                                  (get-in mapping ["list_name"]))
                                                     s-id    (or (:space_id list)
                                                                  (get-in mapping [:space_id])
                                                                  (get-in mapping ["space_id"]))]
                                                 (cond-> m
                                                   l-name (assoc-in [pid :list_name] l-name)
                                                   s-id   (assoc-in [pid :space_id] s-id))))
                                             (:project_mappings data {})
                                             (:project_mappings data {}))]
                     (assoc data :project_mappings enriched-mappings))
                   :else data)]
     {:db (assoc-in db [:entity-mapping :saving?] true)
      :http-xhrio {:method          :put
                   :uri             (broutes/get-url "two_way_integration_settings#update_entity_mapping"
                                                     {:project_id project-id
                                                      :id         integration-id})
                   :params          payload
                   :format          (ajax/json-request-format)
                   :response-format (ajax/json-response-format {:keywords? true})
                   :on-success      [::save-settings-success]
                   :on-failure      [::save-settings-failure]}})))

(re-frame/reg-event-fx
 ::save-settings-success
 (fn [{:keys [db]} [_]]
   (let [current-data       (get-in db [:entity-mapping :data])
         current-selected   (get-in db [:entity-mapping :selected-project-ids])
         current-space-ids  (get-in db [:entity-mapping :selected-space-ids])]
     {:db       (update db :entity-mapping merge
                        {:saving?                    false
                         :saved-data                 current-data
                         :saved-selected-project-ids current-selected
                         :saved-selected-space-ids   current-space-ids
                         :errors                     []})
      :dispatch [::notifications/success2
                 (get-translation "bugtracker.integration-settings.save.success"
                                  "Settings saved successfully")]})))

(re-frame/reg-event-fx
 ::save-settings-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :error]
                               (get-translation "bugtracker.integration-settings.error.save-failed"
                                                "Failed to save entity mapping"))]
     {:db       (assoc-in db [:entity-mapping :saving?] false)
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-db
 ::cancel-changes
 (fn [db [_]]
   (let [saved-data      (get-in db [:entity-mapping :saved-data])
         saved-selected  (get-in db [:entity-mapping :saved-selected-project-ids])
         saved-space-ids (get-in db [:entity-mapping :saved-selected-space-ids])]
     (-> db
         (assoc-in [:entity-mapping :data] saved-data)
         (assoc-in [:entity-mapping :selected-project-ids] saved-selected)
         (assoc-in [:entity-mapping :selected-space-ids] saved-space-ids)))))
